import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { IconLogout, IconX } from '@tabler/icons-react';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';
import { useSignOut } from '@features/Auth/SignOut/hooks/useSignOut';
import type { UserDto } from '@shared/services/apiService';

/** Uses the same preference actions and sign-out flow as the main interface. */
export const DesignAccountPanel = ({ user, onClose }: { user?: UserDto; onClose: () => void }) => {
  const dispatch = useAppDispatch();
  const preferences = useAppSelector((state) => state.customizer);
  const signOut = useSignOut();
  return (
    <Stack gap={3} sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography component="h2" variant="h6" id="design-account-title">
          Account
        </Typography>
        <IconButton aria-label="Close account" onClick={onClose}>
          <IconX size={18} />
        </IconButton>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
          {user?.firstName?.slice(0, 1) || 'A'}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={600}>
              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Account'}
            </Typography>
            <Chip
              label={user?.role === 'viewer' ? 'Viewer' : user?.role || 'Account'}
              size="small"
              variant="outlined"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {user?.email}
          </Typography>
        </Box>
      </Stack>
      <Divider />
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Appearance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preferences for this interface.
        </Typography>
      </Box>
      <Stack gap={1}>
        <Typography variant="body2">Theme</Typography>
        <ToggleButtonGroup
          fullWidth
          exclusive
          size="small"
          aria-label="Account theme"
          value={preferences.activeMode}
          onChange={(_, value) => value && dispatch(actions.customizer.setDarkMode(value))}>
          <ToggleButton value="light">Light</ToggleButton>
          <ToggleButton value="dark">Dark</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack gap={1}>
        <Typography variant="body2">Direction</Typography>
        <ToggleButtonGroup
          fullWidth
          exclusive
          size="small"
          aria-label="Account direction"
          value={preferences.activeDir}
          onChange={(_, value) => value && dispatch(actions.customizer.setDir(value))}>
          <ToggleButton value="ltr">Left to right</ToggleButton>
          <ToggleButton value="rtl">Right to left</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack gap={1}>
        <Typography variant="body2">Cards</Typography>
        <ToggleButtonGroup
          fullWidth
          exclusive
          size="small"
          aria-label="Account cards"
          value={preferences.isCardShadow ? 'shadow' : 'border'}
          onChange={(_, value) =>
            value && dispatch(actions.customizer.setCardShadow(value === 'shadow'))
          }>
          <ToggleButton value="border">Border</ToggleButton>
          <ToggleButton value="shadow">Shadow</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Divider />
      <Button variant="outlined" startIcon={<IconLogout size={16} />} onClick={signOut}>
        Sign out
      </Button>
    </Stack>
  );
};
